import React, { useEffect, useState } from 'react';
import { User, Registration, PaymentOrder, Certificate } from '../types';
import { X, User as UserIcon, LogOut, Award, ClipboardList, CreditCard, Save, RefreshCw } from 'lucide-react';
import { api, PAY_STATUS_TEXT, AUDIT_STATUS_TEXT } from '../api';

interface UserCenterModalProps {
  user: User;
  onClose: () => void;
  onLogout: () => void;
  refreshKey: number;
  onUserUpdated: (u: User) => void;
}

export const UserCenterModal: React.FC<UserCenterModalProps> = ({ user, onClose, onLogout, refreshKey, onUserUpdated }) => {
  const [tab, setTab] = useState<'profile' | 'regs' | 'orders' | 'certs'>('profile');
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [regs, setRegs] = useState<Registration[]>([]);
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [certs, setCerts] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setName(user.name); setPhone(user.phone); }, [user.id, refreshKey]);

  const loadData = async (t: string) => {
    setLoading(true);
    try {
      if (t === 'regs') setRegs(await api<Registration[]>('my_registrations'));
      if (t === 'orders') setOrders(await api<PaymentOrder[]>('my_orders'));
      if (t === 'certs') setCerts(await api<Certificate[]>('my_certificates'));
    } catch { /* ignore */ }
    setLoading(false);
  };

  const switchTab = (t: any) => { setTab(t); loadData(t); };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMsg(''); setSaveErr('');
    try {
      await api('update_profile', { name: name.trim(), phone: phone.trim() }, 'POST');
      setSaveMsg('资料已更新');
      const me = await api<User>('me');
      onUserUpdated(me);
    } catch (ex: any) {
      setSaveErr(ex.message);
    }
  };

  const payBadge = (s: string) => {
    const cls: Record<string, string> = { pending: 'bg-amber-100 text-amber-800', paid: 'bg-emerald-100 text-emerald-800', refunding: 'bg-sky-100 text-sky-800', refunded: 'bg-slate-200 text-slate-600' };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${cls[s] || ''}`}>{PAY_STATUS_TEXT[s] || s}</span>;
  };
  const auditBadge = (s: string) => {
    const cls: Record<string, string> = { pending_audit: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${cls[s] || ''}`}>{AUDIT_STATUS_TEXT[s] || s}</span>;
  };
  const certBadge = (s: string) => {
    const cls: Record<string, string> = { official: 'bg-blue-100 text-blue-700', pending_audit: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-700' };
    const txt: Record<string, string> = { official: '官方证书', pending_audit: '待审核', approved: '已通过', rejected: '已驳回' };
    return <span className={`px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${cls[s] || ''}`}>{txt[s] || s}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl relative my-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 z-10"><X className="w-5 h-5" /></button>

        {/* 头部 */}
        <div className="bg-gradient-to-r from-sky-800 via-blue-900 to-indigo-900 text-white p-6 sm:p-8 rounded-t-3xl">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center text-xl font-black">
              {user.name.slice(0, 1)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-black">{user.name}</h3>
                {user.role === 'member' && user.member_status === 'active'
                  ? <span className="text-[10px] bg-emerald-400 text-emerald-950 px-2 py-0.5 rounded-full font-bold">正式会员</span>
                  : <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-bold">普通用户</span>}
              </div>
              <p className="text-xs text-sky-200 font-mono mt-1">{user.phone}</p>
              {user.member_id && <p className="text-[11px] text-sky-300 mt-1">会员编号 {user.member_id} · 有效期至 {user.membership_expiry || '-'}</p>}
            </div>
            <button onClick={onLogout} className="ml-auto flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-xs font-bold">
              <LogOut className="w-3.5 h-3.5" />退出
            </button>
          </div>
        </div>

        {/* 导航 */}
        <div className="flex border-b border-slate-100 px-4 sm:px-6">
          {([['profile', '个人资料', UserIcon], ['regs', '我的报名', ClipboardList], ['orders', '我的订单', CreditCard], ['certs', '我的证书', Award]] as const).map(([id, label, Icon]) => (
            <button key={id} onClick={() => switchTab(id)}
              className={`flex items-center gap-1.5 px-4 py-3.5 text-xs font-bold border-b-2 transition-colors ${tab === id ? 'text-sky-700 border-sky-600' : 'text-slate-500 border-transparent hover:text-sky-600'}`}>
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        <div className="p-6 sm:p-8 min-h-[240px]">
          {tab === 'profile' && (
            <form onSubmit={saveProfile} className="max-w-md space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">姓名</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">手机号</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={11} className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-sky-500 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">身份证号（不可修改）</label>
                <input type="text" value={user.id_card} disabled className="w-full px-3.5 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm font-mono text-slate-400" />
              </div>
              {saveMsg && <p className="text-xs text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">{saveMsg}</p>}
              {saveErr && <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-xl px-3 py-2">{saveErr}</p>}
              <button type="submit" className="flex items-center gap-2 px-6 py-2.5 bg-sky-600 text-white text-xs font-bold rounded-xl shadow"><Save className="w-4 h-4" />保存资料</button>
            </form>
          )}

          {tab === 'regs' && (
            <div className="space-y-3">
              {loading ? <div className="text-center text-xs text-slate-400 py-10">加载中...</div> : regs.length === 0 ? (
                <div className="text-center text-xs text-slate-400 py-10">暂无报名记录</div>
              ) : regs.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <span className="text-sm font-bold text-slate-900">{r.event_title}</span>
                    <div className="flex gap-1.5">{payBadge(r.payment_status)}{auditBadge(r.audit_status)}</div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-600">
                    <p>项目：{r.event_item}</p>
                    <p>组别：{r.age_group}</p>
                    <p>费用：¥{r.fee.toFixed(2)}</p>
                    <p className="font-mono">订单：{r.order_no}</p>
                    <p className="col-span-2">报名时间：{r.created_at}</p>
                    {r.athlete_no && <p className="text-emerald-700 font-bold">选手编号：{r.athlete_no}</p>}
                    {r.audit_remark && <p className="col-span-full text-slate-500">备注：{r.audit_remark}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'orders' && (
            <div className="space-y-3">
              {loading ? <div className="text-center text-xs text-slate-400 py-10">加载中...</div> : orders.length === 0 ? (
                <div className="text-center text-xs text-slate-400 py-10">暂无订单</div>
              ) : orders.map((o) => (
                <div key={o.id} className="border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{o.title}</div>
                    <div className="text-xs text-slate-500 font-mono mt-1">{o.order_no} · {o.created_at}</div>
                    {o.refund_reason && <div className="text-[11px] text-amber-700 mt-1">退款原因：{o.refund_reason}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-black text-slate-900">¥{o.amount.toFixed(2)}</div>
                    <div className="mt-1">{payBadge(o.status)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'certs' && (
            <div className="space-y-3">
              {loading ? <div className="text-center text-xs text-slate-400 py-10">加载中...</div> : certs.length === 0 ? (
                <div className="text-center text-xs text-slate-400 py-10">暂无证书记录，可前往「证书中心」上传备案</div>
              ) : certs.map((c) => (
                <div key={c.id} className="border border-slate-200 rounded-2xl p-4 flex items-center gap-4 flex-wrap">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><Award className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-slate-900">{c.holder_name}</span>
                      <span className="font-mono text-xs text-slate-500">{c.cert_no}</span>
                      {certBadge(c.status)}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{c.type} / {c.level} · 发证日期 {c.issue_date}</div>
                  </div>
                  {c.file_url && <a href={c.file_url} target="_blank" className="text-xs text-sky-600 font-bold shrink-0">查看附件 →</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
